import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEO } from '@/components/SEO';
import { GoldButton } from '@/components/ui/GoldButton';
import { HeroSocialProof } from '@/components/sections/HeroSocialProof';
import { AvailabilityBadge } from '@/components/sections/AvailabilityBadge';
import { SessionFinder } from '@/components/sections/SessionFinder';
import { TestimonialHighlight } from '@/components/sections/TestimonialHighlight';
import { QuickLinks } from '@/components/sections/QuickLinks';
import { FreeConsultationBanner } from '@/components/sections/FreeConsultationBanner';
import { GoldenParticles } from '@/components/effects/GoldenParticles';
import { MistDivider } from '@/components/effects/MistDivider';
import { AtmosphericBackground } from '@/components/effects/AtmosphericBackground';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { useExitIntent } from '@/hooks/useExitIntent';
const ExitIntentOffer = lazy(() => import('@/components/sections/ExitIntentOffer').then(m => ({ default: m.ExitIntentOffer })));
const heroImage = '/images/hero-bay-sunset.jpg';
// Newest session work for the homepage Featured Work grid (arcade + spring tractor/tulip)
import featArcadeTattooedWomanNeon from '@/assets/portfolio-portrait-arcade-tattooed-woman-neon.jpg';
import featArcadeBeardedManProfile from '@/assets/portfolio-portrait-arcade-bearded-man-profile.jpg';
import featArcadePacmanVsCoupleBw from '@/assets/portfolio-portrait-arcade-pacman-vs-couple-bw.jpg';
import featArcadeWomanNeon from '@/assets/portfolio-portrait-arcade-woman-neon.jpg';
import featArcadeMan from '@/assets/portfolio-portrait-arcade-man.jpg';
import featBoyTulipBench from '@/assets/portfolio-portrait-boy-tulip-bench.jpg';
import featBoyPinkTractor from '@/assets/portfolio-portrait-boy-pink-tractor.jpg';
import featBoyPurpleTractor from '@/assets/portfolio-portrait-boy-purple-tractor.jpg';
import featBoyTractorBw from '@/assets/portfolio-portrait-boy-tractor-bw.jpg';
import featBoyTulipsMtHoodBw from '@/assets/portfolio-portrait-boy-tulips-mthood-bw.jpg';

const newestSessionWork = [
  { id: 'nw1', image: featArcadeTattooedWomanNeon, title: 'Neon Ink', category: 'Arcade Series' },
  { id: 'nw2', image: featBoyTulipBench, title: 'Bench in Bloom', category: 'Tulip Series' },
  { id: 'nw3', image: featArcadePacmanVsCoupleBw, title: '4-Player VS', category: 'Couples Arcade' },
  { id: 'nw4', image: featBoyPinkTractor, title: 'Pink John Deere', category: 'Tractor Series' },
  { id: 'nw5', image: featArcadeBeardedManProfile, title: 'Lost in the Game', category: 'Arcade Series' },
  { id: 'nw6', image: featBoyTulipsMtHoodBw, title: 'Tulip Field', category: 'Tulip Series' },
  { id: 'nw7', image: featArcadeWomanNeon, title: 'Neon Glow', category: 'Arcade Series' },
  { id: 'nw8', image: featBoyPurpleTractor, title: 'For Dad', category: 'Tractor Series' },
  { id: 'nw9', image: featArcadeMan, title: 'High Score', category: 'Arcade Series' },
  { id: 'nw10', image: featBoyTractorBw, title: 'Tractor Cool', category: 'Tractor Series' },
];

const MiniSessionPromo = lazy(() => import('@/components/sections/MiniSessionPromo').then(m => ({ default: m.MiniSessionPromo })));
const SessionShowcase = lazy(() => import('@/components/sections/SessionShowcase').then(m => ({ default: m.SessionShowcase })));
const EmailCapture = lazy(() => import('@/components/sections/EmailCapture').then(m => ({ default: m.EmailCapture })));
const ClientProcess = lazy(() => import('@/components/sections/ClientProcess').then(m => ({ default: m.ClientProcess })));
const Credentials = lazy(() => import('@/components/sections/Credentials').then(m => ({ default: m.Credentials })));
const FeaturedBlogPosts = lazy(() => import('@/components/sections/FeaturedBlogPosts').then(m => ({ default: m.FeaturedBlogPosts })));
const InstagramFeed = lazy(() => import('@/components/sections/InstagramFeed').then(m => ({ default: m.InstagramFeed })));
const GiftCardsSection = lazy(() => import('@/components/sections/GiftCardsSection').then(m => ({ default: m.GiftCardsSection })));
const RecentWork = lazy(() => import('@/components/sections/RecentWork').then(m => ({ default: m.RecentWork })));

const FEATURED_COUNT_OPTIONS = [5, 10, 15] as const;
const FEATURED_LS_KEY = 'rmp:featuredWork:v1';

function readFeaturedSettings(): { count: number; offset: number } {
  if (typeof window === 'undefined') return { count: 5, offset: 0 };
  try {
    const raw = window.localStorage.getItem(FEATURED_LS_KEY);
    if (!raw) return { count: 5, offset: 0 };
    const parsed = JSON.parse(raw);
    const count = (FEATURED_COUNT_OPTIONS as readonly number[]).includes(parsed.count)
      ? parsed.count
      : 5;
    const offset = Number.isFinite(parsed.offset) ? Math.max(0, parsed.offset | 0) : 0;
    return { count, offset };
  } catch {
    return { count: 5, offset: 0 };
  }
}

const LazySection = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="py-16" />}>
    {children}
  </Suspense>
);

export default function Index() {
  const { showExitPopup, closeExitPopup } = useExitIntent();
  const initial = useMemo(readFeaturedSettings, []);
  const [featuredCount, setFeaturedCount] = useState<number>(initial.count);
  const [featuredOffset, setFeaturedOffset] = useState(initial.offset);

  // Persist whenever either changes
  useEffect(() => {
    try {
      window.localStorage.setItem(
        FEATURED_LS_KEY,
        JSON.stringify({ count: featuredCount, offset: featuredOffset })
      );
    } catch {
      /* storage may be disabled */
    }
  }, [featuredCount, featuredOffset]);

  const featuredWork = useMemo(() => {
    const total = newestSessionWork.length;
    if (total === 0) return [];
    const start = featuredOffset % total;
    // Wrap around so refresh always shows N items
    const wrapped = [
      ...newestSessionWork.slice(start),
      ...newestSessionWork.slice(0, start),
    ].slice(0, featuredCount);
    return wrapped;
  }, [featuredCount, featuredOffset]);

  const handleRefreshFeatured = () => {
    setFeaturedOffset((o) => o + featuredCount);
  };

  return (
    <Layout>
      <SEO
        title="Portland Family & Brand Photographer | Raven Moon Photography"
        description="Portland, Oregon's trusted family & brand photographer. Natural, authentic portraits for families, seniors, couples & small businesses. Book a free consultation today!"
        url="/"
        keywords="portland photographer, family photographer portland oregon, brand photographer portland, senior portraits portland, couples photographer portland OR, affordable photographer portland"
      />

      {showExitPopup && (
        <LazySection>
          <ExitIntentOffer onClose={closeExitPopup} />
        </LazySection>
      )}

      {/* ═══════════════ HERO — Full-bleed immersive ═══════════════ */}
      <section className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden" aria-label="Hero">
        {/* Background image */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-muted animate-pulse" />
          <img
            src={heroImage}
            alt="Portland Oregon skyline at golden hour — Raven Moon Photography"
            className="w-full h-full object-cover relative z-[1]"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="sync"
          />
          {/* Dramatic overlay with violet-blue-black gradient */}
          <div className="absolute inset-0 z-[2]" style={{
            background: `
              linear-gradient(to bottom, hsl(228 30% 5% / 0.75) 0%, hsl(228 25% 5% / 0.3) 40%, hsl(228 25% 5% / 0.5) 70%, hsl(228 25% 5%) 100%),
              radial-gradient(ellipse at 20% 50%, hsl(270 35% 20% / 0.3), transparent 60%),
              radial-gradient(ellipse at 80% 30%, hsl(42 65% 52% / 0.08), transparent 50%)
            `
          }} />
        </div>

        {/* Floating particles */}
        <GoldenParticles count={35} className="z-[3]" />

        {/* Hero content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 sm:px-4 w-full max-w-5xl mx-auto pt-20 pb-8">
          {/* Small decorative accent above title */}
          <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in-up">
            <span className="block w-12 h-px bg-gradient-to-r from-transparent to-primary/50" />
            <span className="font-cinzel text-[10px] sm:text-xs tracking-[0.4em] uppercase text-primary/80">Portland, Oregon</span>
            <span className="block w-12 h-px bg-gradient-to-l from-transparent to-primary/50" />
          </div>

          {/* Main title — Cinzel Decorative */}
          <h1 className="font-display text-[2.2rem] xs:text-5xl sm:text-6xl md:text-8xl lg:text-9xl text-foreground mb-6 animate-fade-in-up text-ethereal leading-[1.05] w-full text-center font-bold drop-shadow-[0_0_30px_hsl(42_65%_52%/0.3)]">
            <span className="block text-center">Raven Moon</span>
            <span className="block text-center text-gradient-gold mt-1" style={{ display: 'block', textAlign: 'center' }}>Photography</span>
          </h1>

          {/* Filigree divider */}
          <div className="filigree-divider max-w-xs mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <span className="text-primary/60 text-lg">✦</span>
          </div>

          {/* Subtitle — italic serif */}
          <p
            className="font-accent text-base sm:text-lg md:text-xl text-foreground/75 mb-2 max-w-2xl mx-auto animate-fade-in-up"
            style={{ animationDelay: '0.3s' }}
          >
            Moody, magical legacy portraits your family will treasure for generations
          </p>
          <p
            className="text-xs sm:text-sm tracking-[0.3em] uppercase text-primary/70 mb-8 animate-fade-in-up font-cinzel"
            style={{ animationDelay: '0.35s' }}
          >
            Custom Sessions · Worldwide Travel
          </p>

          {/* CTA buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 items-center justify-center animate-fade-in-up"
            style={{ animationDelay: '0.5s' }}
          >
            <Link to="/booking" className="w-full sm:w-auto flex justify-center">
              <GoldButton size="lg" className="w-full max-w-xs sm:w-auto min-h-[52px] glow-hover tracking-wider">
                Book Your Session
              </GoldButton>
            </Link>
            <Link to="/contact?consultation=true" className="w-full sm:w-auto flex justify-center">
              <GoldButton variant="outline" size="lg" className="w-full max-w-xs sm:w-auto min-h-[52px] glow-hover-subtle tracking-wider">
                Free Consultation
              </GoldButton>
            </Link>
          </div>

          <div className="mt-5 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <HeroSocialProof variant="compact" />
          </div>
          <div className="mt-2 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
            <AvailabilityBadge variant="hero" />
          </div>
        </div>

        {/* Mist at bottom */}
        <MistDivider variant="bottom" intensity="strong" />
      </section>

      {/* ═══════════════ QUICK LINKS ═══════════════ */}
      <ScrollReveal>
        <QuickLinks />
      </ScrollReveal>

      {/* ═══════════════ SESSION SHOWCASE ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <SessionShowcase />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ SESSION FINDER ═══════════════ */}
      <div className="relative overflow-hidden">
        <AtmosphericBackground variant="aurora" />
        <ScrollReveal>
          <SessionFinder />
        </ScrollReveal>
        <MistDivider variant="bottom" intensity="light" />
      </div>

      {/* ═══════════════ FREE CONSULTATION ═══════════════ */}
      <ScrollReveal>
        <FreeConsultationBanner />
      </ScrollReveal>

      {/* ═══════════════ EMAIL CAPTURE ═══════════════ */}
      <LazySection>
        <div className="relative overflow-hidden">
          <AtmosphericBackground variant="moonlight" />
          <ScrollReveal>
            <EmailCapture />
          </ScrollReveal>
        </div>
      </LazySection>

      {/* ═══════════════ TESTIMONIAL ═══════════════ */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, hsl(270 30% 15% / 0.15), transparent 70%)'
        }} />
        <ScrollReveal>
          <TestimonialHighlight />
        </ScrollReveal>
      </div>

      {/* ═══════════════ GIFT CARDS ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <GiftCardsSection />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ MINI SESSION PROMO ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <MiniSessionPromo />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ CLIENT PROCESS ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <ClientProcess />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ RECENT WORK — latest sessions ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <RecentWork />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ FEATURED WORK — Immersive gallery ═══════════════ */}
      <section className="relative py-28 overflow-hidden" aria-label="Featured work">
        <AtmosphericBackground variant="nebula" />
        <MistDivider variant="top" intensity="medium" />

        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="text-center mb-20">
              <span className="font-cinzel text-[10px] tracking-[0.4em] uppercase text-primary/70 block mb-4">
                The Collection
              </span>
              <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-foreground mb-5 text-ethereal">
                Featured Work
              </h2>
              <div className="filigree-divider max-w-xs mx-auto mb-5">
                <span className="text-primary/50 text-sm">✦</span>
              </div>
              <p className="font-accent text-lg text-muted-foreground max-w-2xl mx-auto">
                Real moments. Real people. Real magic.
              </p>
            </div>
          </ScrollReveal>

          {/* Count selector + refresh */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <div className="flex items-center gap-1 border border-primary/20 rounded-full p-1 bg-background/40 backdrop-blur-sm">
              {FEATURED_COUNT_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => { setFeaturedCount(n); setFeaturedOffset(0); }}
                  aria-pressed={featuredCount === n}
                  className={`min-h-[36px] px-3 text-xs font-cinzel tracking-[0.2em] uppercase rounded-full transition ${
                    featuredCount === n
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Show {n}
                </button>
              ))}
            </div>
            <button
              onClick={handleRefreshFeatured}
              aria-label="Refresh featured work"
              className="inline-flex items-center gap-2 min-h-[36px] px-4 text-xs font-cinzel tracking-[0.2em] uppercase rounded-full border border-primary/30 text-primary/90 hover:bg-primary/10 transition"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {featuredWork.map((work, index) => (
              <ScrollReveal key={work.id} delay={index * 120}>
                <Link
                  to="/portfolio"
                  aria-label={`${work.title} — ${work.category} photography`}
                  className="group relative overflow-hidden aspect-[3/4] block"
                >
                  <img
                    src={work.image}
                    alt={work.title}
                    loading="lazy"
                    decoding="async"
                    width={400}
                    height={533}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 20vw"
                    className="w-full h-full object-cover transition-all duration-[1.2s] group-hover:scale-110"
                  />
                  {/* Ethereal overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  {/* Gold border glow */}
                  <div className="absolute inset-0 border border-primary/0 group-hover:border-primary/30 transition-all duration-700 pointer-events-none" />
                  <div className="absolute inset-[4px] border border-primary/0 group-hover:border-primary/10 transition-all duration-700 pointer-events-none" />
                  {/* Text overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out">
                    <span className="font-cinzel text-[10px] uppercase tracking-[0.3em] text-primary/80">
                      {work.category}
                    </span>
                    <h3 className="font-serif text-xl text-foreground mt-1">{work.title}</h3>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <div className="text-center mt-14">
              <Link to="/portfolio">
                <GoldButton variant="outline" className="glow-hover-subtle tracking-wider">
                  Explore Full Portfolio <ArrowRight className="ml-2 h-4 w-4 inline" />
                </GoldButton>
              </Link>
            </div>
          </ScrollReveal>
        </div>

        <MistDivider variant="bottom" intensity="medium" />
      </section>





      {/* ═══════════════ CREDENTIALS ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <Credentials />
        </ScrollReveal>
      </LazySection>


      {/* ═══════════════ BLOG POSTS ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <FeaturedBlogPosts />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ INSTAGRAM ═══════════════ */}
      <LazySection>
        <ScrollReveal>
          <InstagramFeed />
        </ScrollReveal>
      </LazySection>

      {/* ═══════════════ FINAL CTA — Full atmosphere ═══════════════ */}
      <section className="relative py-28 overflow-hidden" aria-label="Call to action">
        <GoldenParticles count={25} />
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse at 30% 50%, hsl(270 30% 15% / 0.2), transparent 60%),
            radial-gradient(ellipse at 70% 30%, hsl(42 50% 30% / 0.08), transparent 50%)
          `
        }} />

        <ScrollReveal>
          <div className="container mx-auto px-4 text-center relative z-10">
            <span className="font-cinzel text-[10px] tracking-[0.4em] uppercase text-primary/70 block mb-4">
              Your Story Awaits
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-4 text-ethereal">
              Okay, You've Scrolled
              <span className="text-gradient-gold ml-2">This Far</span>
            </h2>
            <div className="filigree-divider max-w-xs mx-auto mb-6">
              <span className="text-primary/50 text-sm">✦</span>
            </div>
            <p className="font-accent text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              That means something. Whether it's a family session, a brand shoot, or just a 
              "hey, I've been thinking about this for months" — let's make it happen.
            </p>
            <Link to="/booking">
              <GoldButton size="lg" className="glow-hover animate-glow-pulse tracking-wider">
                Let's Do This
              </GoldButton>
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </Layout>
  );
}